import * as CONSTANTS from '../../constants';
import gatewayService from '../../../services/GatewayService';
import tokenService from '../../../services/TokenStorageService';
import IPService from '../../../services/IPService';
import {getBrowserName} from "../../../services/BrowserService";
import {
    getContractManagementData,
    getFormSaveData,
    convertContractManagementData,
    getContractManagementProgress,
    extractFileName,
    getExtensionFromMIMEType,
    mergeFileLists,
    extractFileAndExtension,
    JSONClone,
    getCurrentContractYear,
    mapContractAcceptedProgressParts,
    progressPartList
} from "../../../services/ConverterService";
import router from '../../../routes';
import {saveAs} from 'file-saver/FileSaver';
import { some, map } from 'lodash-es';

/**
 * Get contract years.
 *
 * @param commit
 * @param state
 * @returns {Promise<T | never>}
 */
export const getContractYears = ({commit, state}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_OVERVIEW_PENDING);
    const token = tokenService.get();

    return gatewayService.getContractYears(token).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_YEARS_SUCCESS, res.data.data);
            const currentYear = getCurrentContractYear();
            const hasYear = some(state.overview.years, (year) => {
                return currentYear === year
            });
            if (hasYear) {
                contractManagementListYearSet({commit, state}, currentYear);
            } else {
                contractManagementListYearSet({commit, state}, state.overview.years[0]);
            }
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_YEARS_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_OVERVIEW_FAILURE);
    })
};

/**
 * Get contracts overview.
 *
 * @param commit
 * @param state
 * @returns {Promise<T>}
 */
export const getContractManagementssOverview = ({commit, state}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_OVERVIEW_PENDING);
    const token = tokenService.get();
    const year = state.overview.year;
    return gatewayService.getContractManagementssOverview(token, year).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_OVERVIEW_SUCCESS, res.data.data)
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_OVERVIEW_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_OVERVIEW_FAILURE);
    })
};

/**
 * Get contract data by contract id.
 *
 * @param commit
 * @param payload
 * @returns {Promise.<T>}
 */
export const getContractManagementByContractId = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_DATA_PENDING);
    const token = tokenService.get();

    return gatewayService.getContractManagementByContractId(token, payload).then((res) => {
        if (res.status === 200) {
            const contractData = convertContractManagementData(res.data.data, payload);
            const progress = getContractManagementProgress(contractData);
            commit(CONSTANTS.CONTRACT_MANAGEMENT_PROGRESS_SET, progress);
            commit(CONSTANTS.CONTRACT_MANAGEMENT_DATA_SUCCESS, {item: JSONClone(contractData), form: contractData});
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_DATA_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_DATA_FAILURE);
    })
};

/**
 * Get the contract data for a specific contract id.
 *
 * @param commit
 * @param state
 * @param payload
 */
export const contractManagementListContractSelect = ({commit, state}, payload) => {
    const contractData = getContractManagementData(state.overview.items, payload);
    commit(CONSTANTS.CONTRACT_MANAGEMENT_DATA_SUCCESS, {item: JSONClone(contractData), form: contractData});
    commit(CONSTANTS.CONTRACT_MANAGEMENT_PROGRESS_SET, getContractManagementProgress(contractData));
    // When the data is set in state then redirect
    router.push('/dashboard/contract-management/' + payload);
};

/**
 * Set the filter option for the contract overview list.
 *
 * @param commit
 * @param payload
 */
export const contractManagementListFilterSelect = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FILTER_SET, payload);
};

/**
 * Contract list limit set.
 *
 * @param commit
 * @param payload
 */
export const contractManagementListLimitSet = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_LIST_LIMIT_SET, payload);
};

/**
 * Set the year a contracts are fetched for and fetch the contracts.
 *
 * @param commit
 * @param state
 * @param payload
 */
export const contractManagementListYearSet = ({commit, state}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_LIST_YEAR_SET, payload);
    getContractManagementssOverview({commit, state});
};

/**
 * Contract list page increment.
 *
 * @param commit
 */
export const contractManagementListPageIncrement = ({commit}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_LIST_PAGE_INCREMENT);
};

/**
 * Contract list page decrement.
 *
 * @param commit
 */
export const contractManagementListPageDecrement = ({commit}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_LIST_PAGE_DECREMENT);
};

/**
 * Edit the form data.
 *
 * @param commit
 * @param state
 * @param payload
 */
export const contractManagementFormSet = ({commit, state}, payload) => {
    if (payload.hasOwnProperty('files')) {
        payload.files = state.contractManagement.form.part === 'basic' || state.contractManagement.form.subPart === 'arrivalDeparture' ?
            mergeFileLists(state.contractManagement.form[payload.part][payload.subPart].files, payload.files) :
            mergeFileLists(state.contractManagement.form[payload.part][payload.subPart][state.contractManagement.form.index].files, payload.files);
        commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_SET, payload);
    } else if (payload.hasOwnProperty('images')) {
        payload.images = mergeFileLists(state.contractManagement.form[payload.part][payload.subPart][state.contractManagement.form.index].images, payload.images);
        commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_SET, payload);
    } else {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_SET, payload);
    }
};

/**
 * Remove contract element from lists.
 *
 * @param commit
 * @param payload
 */
export const contractManagementFormUnset = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_UNSET, payload);
};

/**
 * Set the contract form part.
 *
 * @param commit
 * @param payload
 */
export const setContractManagementFormPart = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_PART_SET, payload);
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_INDEX_RESET);
};

/**
 * Contract form page forward.
 *
 * @param commit
 * @param payload
 */
export const contractManagementFormPageSet = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_PAGE_SET, payload);
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_INDEX_RESET);
};

/**
 * Remove file from file list.
 *
 * @param commit
 * @param payload
 */
export const contractManagementFormFileRemove = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_FILE_REMOVE, payload);
};

/**
 * Contract form save.
 *
 * @param commit
 * @param state
 */
export const contractManagementFormSave = ({commit, state}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_SAVE_PENDING);
    const token = tokenService.get();
    const contractId = state.contractManagement.item.contractId;
    const formData = getFormSaveData(state.contractManagement.form);

    return gatewayService.saveContractManagementByContractId(token, formData, contractId).then((res) => {
        if (res.status === 200) {
            const contractData = convertContractManagementData(res.data.data, res.data.data.contractId);
            const progress = getContractManagementProgress(contractData);
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_SAVE_SUCCESS, {
                item: JSONClone(contractData),
                form: contractData
            });
            commit(CONSTANTS.CONTRACT_MANAGEMENT_PROGRESS_SET, progress);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'saved',
                level: 'success'
            }, {root: true});
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_SAVE_NO_CONTENT);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'notFound',
                level: 'warning'
            }, {root: true});
        }
    }).catch((err) => {
        if (err.status === 400) {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_SAVE_FAILURE);
            if (err.data.code === 1) {
                commit('alert/ALERT_COUNTDOWN_RESET', {
                    message: 'validationError',
                    level: 'danger'
                }, {root: true});
            } else if (err.data.code === 2) {
                commit('alert/ALERT_COUNTDOWN_RESET', {
                    message: 'unsupportedType',
                    level: 'danger'
                }, {root: true});
            }
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_SAVE_FAILURE);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'error',
                level: 'danger'
            }, {root: true});
        }
    })
};

/**
 * Download file from s3.
 *
 * @param commit
 * @param state
 * @param payload
 * @returns {Promise<T>}
 */
export const contractManagementFormFileDownload = ({commit, state}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_FILE_DOWNLOAD_PENDING);
    const token = tokenService.get();
    const file = payload.part === 'basic' || payload.subPart === 'arrivalDeparture' ?
        state.contractManagement.form[payload.part][payload.subPart]['s3'][payload.index] :
        state.contractManagement.form[payload.part][payload.subPart][payload.subIndex]['s3'][payload.index];
    const fileName = extractFileName(file);
    return gatewayService.contractManagementFormFileDownload(token, fileName).then((res) => {
        if (res.status === 200) {
            const fileType = getExtensionFromMIMEType(res.headers['content-type']);
            let file = undefined;
            if (document.documentMode || /Edge/.test(navigator.userAgent)) {
                file = new Blob([res.data], fileName + "." + fileType, {type: res.headers['content-type']});
            } else {
                file = new File([res.data], fileName + "." + fileType, {type: res.headers['content-type']});
            }
            saveAs(file, fileName + "." + fileType, false);
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_FILE_DOWNLOAD_SUCCESS);
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_FILE_DOWNLOAD_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_FILE_DOWNLOAD_FAILURE);
    });
};

/**
 * Remove the file object from state.
 *
 * @param commit
 * @param state
 * @param payload
 */
export const contractManagementFormFileDelete = ({commit, state}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_FILE_DELETE_PENDING);
    const token = tokenService.get();
    const file = payload.part === 'basic' || payload.subPart === 'arrivalDeparture' ?
        state.contractManagement.form[payload.part][payload.subPart]['s3'][payload.index] :
        payload.type === 'image' ?
            state.contractManagement.form[payload.part][payload.subPart][payload.subIndex]['imagesS3'][payload.index] :
            state.contractManagement.form[payload.part][payload.subPart][payload.subIndex]['s3'][payload.index];
    const fileObj = extractFileAndExtension(file);

    return gatewayService.removeFileFromS3(token, fileObj.name, fileObj.extension, payload.contractId).then((res) => {
        if (res.status === 200) {
            const contractData = convertContractManagementData(res.data.data, res.data.data.contractId);
            const progress = getContractManagementProgress(contractData);
            commit(CONSTANTS.CONTRACT_MANAGEMENT_PROGRESS_SET, progress);
            commit(CONSTANTS.CONTRACT_MANAGEMENT_DATA_SUCCESS, {item: JSONClone(contractData), form: contractData});
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_FILE_DELETE_SUCCESS);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'removed',
                level: 'success'
            }, {root: true});
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_FILE_DELETE_NO_CONTENT, payload);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'noFile',
                level: 'warning'
            }, {root: true});
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_FILE_DELETE_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    });
};

/**
 * Contract send email.
 *
 * @param commit
 * @param state
 * @returns {Promise<T>}
 */
export const contractManagementSendEmailBackOffice = ({commit, state}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_PENDING);
    const token = tokenService.get();
    const contractId = state.contractManagement.item.contractId;

    return gatewayService.contractManagementSendEmailBackOffice(token, contractId).then((res) => {
        if (res.status === 200) {
            const contractData = convertContractManagementData(res.data.data, res.data.data.contractId);
            commit(CONSTANTS.CONTRACT_MANAGEMENT_DATA_SUCCESS, {item: JSONClone(contractData), form: contractData});
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_SUCCESS);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'emailSent',
                level: 'success'
            }, {root: true});
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_NO_CONTENT);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'emailAlreadySent',
                level: 'warning'
            }, {root: true});
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    })
};

/**
 * Contract send email.
 *
 * @param commit
 * @param state
 * @returns {Promise<T>}
 */
export const contractManagementSendEmailReview = ({commit, state}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_PENDING);
    const token = tokenService.get();
    const contractId = state.contractManagement.item.contractId;

    return gatewayService.contractManagementSendEmailReview(token, contractId).then((res) => {
        if (res.status === 200) {
            const contractData = convertContractManagementData(res.data.data, res.data.data.contractId);
            commit(CONSTANTS.CONTRACT_MANAGEMENT_DATA_SUCCESS, {item: JSONClone(contractData), form: contractData});
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_SUCCESS);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'emailSent',
                level: 'success'
            }, {root: true});
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_NO_CONTENT);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'emailAlreadySent',
                level: 'warning'
            }, {root: true});
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    })
};

/**
 * Send contract ready for supplier email.
 *
 * @param commit
 * @param state
 */
export const contractManagementSendEmailSupplier = ({commit, state}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_PENDING);
    const token = tokenService.get();
    const contractId = state.contract.item.id;

    return gatewayService.contractManagementSendEmailSupplier(token, contractId).then((res) => {
        if (res.status === 200) {
            // commit(CONSTANTS.CONTRACT_MANAGEMENT_DATA_SUCCESS, {item: JSONClone(res.data.data), form: res.data.data});
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_SUCCESS);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'emailSent',
                level: 'success'
            }, {root: true});
            router.push('/dashboard/contract-management/');
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_NO_CONTENT);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'emailAlreadySent',
                level: 'warning'
            }, {root: true});
        }
    }).catch((err) => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_EMAIL_FAILURE);
        if (err.status === 400) {
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'supplier',
                level: 'danger'
            }, {root: true});
        } else {
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'error',
                level: 'danger'
            }, {root: true});
        }
    })
};

/**
 * Add a new element to the contract form.
 *
 * @param commit
 * @param state
 */
export const contractManagementFormAddElement = ({commit}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_ELEMENT_ADD);
};

/**
 * Remove an element from the contract form.
 *
 * @param commit
 * @param payload
 * @param state
 */
export const contractManagementFormRemoveElement = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_ELEMENT_REMOVE, payload);
};

/**
 * Contract form set index.
 *
 * @param commit
 * @param payload
 */
export const contractManagementFormSetIndex = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_FORM_INDEX_SET, payload);
};

/**
 * Get the contract supplier data.
 *
 * @param commit
 * @param state
 * @returns {Promise<T>}
 */
export const getContractManagementSupplierData = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_SUPPLIER_DATA_PENDING);
    const token = tokenService.get();

    return gatewayService.getContractManagementSupplierData(token, payload).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_SUPPLIER_DATA_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_SUPPLIER_DATA_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_SUPPLIER_DATA_FAILURE);
    })
};

/**
 * Copy existing sites or accommodations for a supplier.
 *
 * @param commit
 * @param getters
 * @param state
 * @returns {*}
 */
export const contractManagementCopyExisting = ({commit, getters, state}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_COPY_EXISTING_PENDING);
    const token = tokenService.get();
    const supplierId = state.contractManagement.item.supplierId;
    let siteIds = null;

    switch (state.contractManagement.form.subPart) {
    case 'siteInfo':
        return gatewayService.getContractManagementCopiedSites(token, supplierId).then((res) => {
            if (res.status === 200) {
                commit(CONSTANTS.CONTRACT_MANAGEMENT_COPY_EXISTING_SITE_SUCCESS, res.data.data);
            } else {
                commit(CONSTANTS.CONTRACT_MANAGEMENT_COPY_EXISTING_NO_CONTENT);
                commit('alert/ALERT_COUNTDOWN_RESET', {
                    message: 'noSitesFound',
                    level: 'warning'
                }, {root: true});
            }
        }).catch(() => {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_COPY_EXISTING_FAILURE);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'error',
                level: 'error'
            }, {root: true});
        });
    case 'accInfo':
        siteIds = map(getters['getContractManagementSites'], 'id');
        return gatewayService.getContractManagementCopiedAccommodations(token, supplierId, siteIds).then((res) => {
            if (res.status === 200) {
                commit(CONSTANTS.CONTRACT_MANAGEMENT_COPY_EXISTING_ACCOMMODATION_SUCCESS, res.data.data);
            } else {
                commit(CONSTANTS.CONTRACT_MANAGEMENT_COPY_EXISTING_NO_CONTENT);
                commit('alert/ALERT_COUNTDOWN_RESET', {
                    message: 'noAccommodationsFound',
                    level: 'warning'
                }, {root: true});
            }
        }).catch(() => {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_COPY_EXISTING_FAILURE);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'error',
                level: 'error'
            }, {root: true});
        });
    }
};

/**
 * Set the default text to no changes to indicate that nothing changed.
 *
 * @param commit
 * @param payload
 */
export const contractManagementSetNoChanges = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_NO_CHANGES_SET, payload);
};

/**
 * Get a contract by supplier id.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const getContractBySupplierId = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_DATA_PENDING);
    const token = tokenService.get();

    return gatewayService.getContractBySupplierId(token, payload.supplierId, payload.contractId).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.CONTRACT_DATA_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.CONTRACT_DATA_NO_CONTENT);
        }
    }).catch((err) => {
        if (err.data && err.data.code === 1) {
            commit(CONSTANTS.CONTRACT_DATA_PRODUCT_ERROR);
        } else {
            commit(CONSTANTS.CONTRACT_DATA_FAILURE);
        }
    })
};

/**
 * Get contract notes.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const getContractNotes = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_NOTES_PENDING);
    const token = tokenService.get();

    return gatewayService.getContractNotes(token, payload.contractId).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.CONTRACT_NOTES_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.CONTRACT_NOTES_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_NOTES_FAILURE);
    })
};

/**
 * Get contract history by supplier id.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const getContractHistoryBySupplierId = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_HISTORY_PENDING);
    const token = tokenService.get();

    return gatewayService.getContractHistoryBySupplierId(token, payload).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.CONTRACT_HISTORY_SUCCESS, res.data.data)
        } else {
            commit(CONSTANTS.CONTRACT_HISTORY_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_HISTORY_FAILURE)
    })
};

/**
 * Set the contract part.
 *
 * @param commit
 * @param payload
 */
export const setContractFormPart = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_FORM_PART_SET, payload);
};

/**
 * Accept the contract part, and jump to next part if possible
 *
 * @param commit
 * @param state
 * @param dispatch
 * @param payload
 */
export const contractPartAccept = ({commit, state}, payload) => {
    const token = tokenService.get();
    const contractId = state.contract.item.id;
    const progressObj = state.contract.progress.item;

    commit(CONSTANTS.CONTRACT_FORM_PART_ACCEPT_PENDING);

    return gatewayService.acceptContractPart(token , contractId, {field: progressPartList.indexOf(payload) + 1}).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.CONTRACT_FORM_PART_ACCEPT_SUCCESS);
            commit(CONSTANTS.CONTRACT_ACCEPT_LIST_SUCCESS, mapContractAcceptedProgressParts(res.data.data, progressObj));
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'saved',
                level: 'success'
            }, {root: true});
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_FORM_PART_ACCEPT_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    });
};

/**
 * Set the notes of the contract part.
 *
 * @param commit
 * @param state
 * @param payload
 */
export const contractNotesSet = ({commit, state}, payload) => {
    const part = state.contract.part;
    commit(CONSTANTS.CONTRACT_FORM_NOTES_SET, {part: part, note: payload});
};

/**
 * Finish the contract.
 *
 * @param commit
 * @param state
 * @returns {Promise<T>}
 */
export const acceptContract = ({commit, state}) => {
    commit(CONSTANTS.CONTRACT_FINISH_PENDING);
    const token = tokenService.get();
    const supplierId = state.contract.item.supplierId;
    const contractId = state.contract.item.id;

    // Fetch ip information for the client
    return IPService.getIpInfo().then((res) => {
        const browser = getBrowserName();
        const ip = res.data.ip;
        return gatewayService.acceptContract(token, supplierId, contractId, ip, browser).then(() => {
            commit(CONSTANTS.CONTRACT_FINISH_SUCCESS);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'completed',
                level: 'success'
            }, {root: true});
            router.push('/dashboard/contract/' + supplierId);
        }).catch(() => {
            commit(CONSTANTS.CONTRACT_FINISH_FAILURE);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'error',
                level: 'danger'
            }, {root: true});
        })
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_FINISH_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    });
};

/**
 * Download the contract.
 *
 * @param commit
 * @param payload
 * @returns {*|AxiosPromise<any>}
 */
export const downloadContract = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_DOWNLOAD_PENDING);
    const token = tokenService.get();
    const fileName = extractFileName(payload);

    return gatewayService.downloadContract(token, fileName).then((res) => {
        if (res.status === 200) {
            const fileType = getExtensionFromMIMEType(res.headers['content-type']);
            let file = undefined;
            if (document.documentMode || /Edge/.test(navigator.userAgent)) {
                file = new Blob([res.data], fileName + "." + fileType, {type: res.headers['content-type']});
            } else {
                file = new File([res.data], fileName + "." + fileType, {type: res.headers['content-type']});
            }
            saveAs(file, fileName + "." + fileType, false);
            commit(CONSTANTS.CONTRACT_DOWNLOAD_SUCCESS);
        } else {
            commit(CONSTANTS.CONTRACT_DOWNLOAD_FAILURE);
        }

    }).catch(() => {
        commit(CONSTANTS.CONTRACT_DOWNLOAD_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    })
};

/**
 * Get brands.
 *
 * @param commit
 * @returns {Promise<T | never>}
 */
export const getBrands = ({commit}) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_GET_BRANDS_PENDING);
    const token = tokenService.get();

    return gatewayService.getBrands(token).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_GET_BRANDS_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.CONTRACT_MANAGEMENT_GET_BRANDS_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_MANAGEMENT_GET_BRANDS_FAILURE);
    })
};

/**
 * Add a brand. Payload is the id of the added brand.
 *
 * @param commit
 * @param payload
 */
export const addBrand = ({commit}, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_ADD_BRAND, +payload);
};

/**
 * Remove a brand. Payload is the index of the brand in the array.
 *
 * @param commit
 * @param state
 * @param payload
 */
export const removeBrand = ({commit, state}, payload) => {
    let brands = state.contractManagement.form.extended.brands;
    commit(CONSTANTS.CONTRACT_MANAGEMENT_REMOVE_BRAND, brands.indexOf(+payload))
};

/**
 * Resets contract management state.
 *
 * @param commit
 */
export const resetContractManagementState = ({ commit }) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_RESET_STATE)
};

/**
 * Set the contract note object.
 *
 * @param commit
 * @param payload
 */
export const contractNoteSet = ({ commit }, payload) => {
    commit(CONSTANTS.CONTRACT_NOTE_SET, payload);
};

/**
 * Create a contract note.
 *
 * @param commit
 * @param state
 */
export const createContractNote = ({ commit, state }) => {
    commit(CONSTANTS.CONTRACT_NOTE_CREATE_PENDING);
    const token = tokenService.get();
    const contractId = state.contract.item.id;
    const note = state.contract.note.item;

    return gatewayService.createContractNote(token, contractId, note).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.CONTRACT_NOTE_CREATE_SUCCESS);
            commit(CONSTANTS.CONTRACT_NOTES_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.CONTRACT_NOTE_CREATE_FAILURE);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'error',
                level: 'danger'
            }, {root: true});
        }
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_NOTE_CREATE_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    });
};

/**
 * Get contract accepted parts.
 *
 * @param commit
 * @param state
 * @param payload
 * @returns {Promise<T | never>}
 */
export const getContractAcceptedParts = ({ commit, state }, payload) => {
    commit(CONSTANTS.CONTRACT_ACCEPT_LIST_PENDING);
    const token = tokenService.get();
    const progressObj = state.contract.progress.item;

    return gatewayService.getContractAcceptedParts(token, payload.contractId).then((res) => {
        commit(CONSTANTS.CONTRACT_ACCEPT_LIST_SUCCESS, mapContractAcceptedProgressParts(res.data.data, progressObj));
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_ACCEPT_LIST_FAILURE);
    });
};

/**
 * Send notification email.
 *
 * @param commit
 * @param state
 */
export const sendContractEmailNotification = ({ commit, state }) => {
    commit(CONSTANTS.CONTRACT_NOTE_EMAIL_PENDING);
    const token = tokenService.get();
    const supplierId = state.contract.item.supplierId;
    const contractId = state.contract.item.id;

    return gatewayService.sendContractEmailNotification(token, supplierId, contractId).then(() => {
        commit(CONSTANTS.CONTRACT_NOTE_EMAIL_SUCCESS);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'emailSent',
            level: 'success'
        }, {root: true});
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_NOTE_EMAIL_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    })
};

/**
 * Set the show mine or all contracts filter set.
 *
 * @param commit
 * @param payload
 */
export const contractManagementListShowSet = ({ commit }, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_LIST_SHOW_SET, payload);
};

/**
 * Set the search object in state.
 *
 * @param commit
 * @param payload
 */
export const contractManagementListSearchSet = ({ commit }, payload) => {
    commit(CONSTANTS.CONTRACT_MANAGEMENT_LIST_SEARCH_SET, payload);
};

/**
 * Preview supplier contract draft.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T | never>}
 */
export const previewContractDraft = ({ commit }, payload) => {
    const token = tokenService.get();
    commit(CONSTANTS.CONTRACT_DRAFT_PENDING);
    return gatewayService.previewContractDraft(payload.supplierId, payload.contractId, token).then((res) => {
        const fileName = 'contract-preview';
        const fileType = getExtensionFromMIMEType(res.headers['content-type']);
        let file = undefined;
        if (document.documentMode || /Edge/.test(navigator.userAgent)) {
            file = new Blob([res.data], fileName + "." + fileType, {type: res.headers['content-type']});
        } else {
            file = new File([res.data], fileName + "." + fileType, {type: res.headers['content-type']});
        }
        saveAs(file, fileName + "." + fileType, false);

        commit(CONSTANTS.CONTRACT_DRAFT_SUCCESS, res.data);
    }).catch(() => {
        commit(CONSTANTS.CONTRACT_DRAFT_FAILURE);
    })
}



// WEBPACK FOOTER //
// ./src/store/modules/contract/actions.js